import { useEffect, useState } from 'react';
import { useT } from '@/i18n/useT';
import { errorMessage } from '@/lib/errorMessage';
import { useBotState, useSetMartingale, useSetSafety, useSetPayout, useSetRateLimit } from '@/bot/hooks';
import { BotGate } from './BotGate';
import { BotNav } from './BotNav';

/**
 * Strategy: the settings that decide how the bot risks money.
 *
 * Everything is saved explicitly rather than on change. These values move real
 * stakes, so an accidental keystroke must not become a live setting, and the
 * customer should see what they are committing to before it applies.
 *
 * The bot is the authority: after every save the form re-syncs from the server's
 * response, so a value the engine clamps (multiplier, max steps) shows what
 * actually took effect rather than what was typed.
 */
export function StrategyPage() {
  const { t } = useT();
  const state = useBotState();
  const setMg = useSetMartingale();
  const setSafety = useSetSafety();
  const setPayout = useSetPayout();
  const setRate = useSetRateLimit();

  const [form, setForm] = useState<null | {
    enabled: boolean; multiplier: string; maxSteps: string; baseAmount: string;
    dailyStopLossUsd: string; stopWinUsd: string; payoutThresholdPct: string; tradesPerHour: string;
  }>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  // Seed once from the server, then leave the customer's edits alone: /state
  // polls every few seconds and would otherwise wipe a half-typed value.
  useEffect(() => {
    if (form || !state.data) return;
    const c = state.data.control;
    setForm({
      enabled: c.martingale.enabled,
      multiplier: String(c.martingale.multiplier),
      maxSteps: String(c.martingale.maxSteps),
      baseAmount: String(c.martingale.baseAmount),
      dailyStopLossUsd: String(c.safety.dailyStopLossUsd),
      stopWinUsd: String(c.safety.stopWinUsd),
      payoutThresholdPct: String(c.payoutThresholdPct),
      tradesPerHour: String(c.rateLimit.tradesPerHour),
    });
  }, [state.data, form]);

  const busy = setMg.isPending || setSafety.isPending || setPayout.isPending || setRate.isPending;

  const saveAll = async () => {
    if (!form) return;
    setErr(''); setMsg('');
    // SAFETY: Number('') is 0, and 0 stop-loss / stop-win means "no limit" to
    // the engine. A customer who clears a field to retype it and taps Save
    // would silently disable their own protection. The approved dashboard
    // refused to post a blank or non-numeric limit; so do we. Every field is
    // checked before ANY request goes out, so a bad value never half-saves.
    const num = (s: string): number | null => {
      const v = s.trim();
      if (v === '') return null;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : null;
    };
    const parsed = {
      multiplier: num(form.multiplier),
      maxSteps: num(form.maxSteps),
      baseAmount: num(form.baseAmount),
      dailyStopLossUsd: num(form.dailyStopLossUsd),
      stopWinUsd: num(form.stopWinUsd),
      payoutThresholdPct: num(form.payoutThresholdPct),
      tradesPerHour: num(form.tradesPerHour),
    };
    const bad = (Object.keys(parsed) as Array<keyof typeof parsed>).filter((k) => parsed[k] === null);
    if (bad.length > 0) {
      setErr(t('strategy.invalidFields'));
      return;
    }
    try {
      const r = await setMg.mutateAsync({
        enabled: form.enabled,
        multiplier: parsed.multiplier!,
        maxSteps: parsed.maxSteps!,
        baseAmount: parsed.baseAmount!,
      });
      await setSafety.mutateAsync({
        dailyStopLossUsd: parsed.dailyStopLossUsd!,
        stopWinUsd: parsed.stopWinUsd!,
      });
      await setPayout.mutateAsync(parsed.payoutThresholdPct!);
      await setRate.mutateAsync({ tradesPerHour: parsed.tradesPerHour! });

      // Re-sync from whatever the engine actually accepted.
      const c = r.state.control;
      setForm((f) => f && ({
        ...f,
        multiplier: String(c.martingale.multiplier),
        maxSteps: String(c.martingale.maxSteps),
        baseAmount: String(c.martingale.baseAmount),
      }));
      setMsg(t('strategy.saved'));
    } catch (e) {
      setErr(errorMessage(e));
    }
  };

  const field = (k: keyof NonNullable<typeof form>, label: string, extra?: Record<string, unknown>) => (
    <label className="bot-field">
      <span>{label}</span>
      <input
        type="number"
        value={String(form?.[k] ?? '')}
        onChange={(e) => setForm((f) => f && ({ ...f, [k]: e.target.value }))}
        disabled={busy}
        {...extra}
      />
    </label>
  );

  return (
    <BotGate query={state}>
      {() => (
        <>
          {err ? <div className="bot-alert error">{err}</div> : null}
          {msg ? <div className="bot-alert ok">{msg}</div> : null}

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('strategy.martingale')}</h2></div>
            <label className="bot-check">
              <input
                type="checkbox"
                checked={form?.enabled ?? false}
                onChange={(e) => setForm((f) => f && ({ ...f, enabled: e.target.checked }))}
                disabled={busy}
              />
              <span>{t('strategy.enabled')}</span>
            </label>
            <div className="bot-grid2">
              {field('multiplier', t('strategy.multiplier'), { step: '0.1', min: '1', max: '5' })}
              {field('maxSteps', t('strategy.maxSteps'), { step: '1', min: '1', max: '10' })}
            </div>
            {field('baseAmount', t('strategy.baseAmount'), { step: '0.01', min: '0.01' })}
          </section>

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('strategy.safety')}</h2></div>
            <div className="bot-grid2">
              {field('dailyStopLossUsd', t('strategy.stopLoss'), { step: '1', min: '0' })}
              {field('stopWinUsd', t('strategy.stopWin'), { step: '1', min: '0' })}
            </div>
            <p className="muted">{t('strategy.zeroOff')}</p>
          </section>

          <section className="bot-card">
            <div className="bot-card-head"><h2>{t('nav.strategy')}</h2></div>
            <div className="bot-grid2">
              {field('payoutThresholdPct', t('strategy.payout'), { step: '1', min: '0', max: '100' })}
              {field('tradesPerHour', t('strategy.rateLimit'), { step: '1', min: '0', max: '1000' })}
            </div>
          </section>

          <button type="button" className="bot-btn primary wide" onClick={saveAll} disabled={busy || !form}>
            {busy ? t('common.saving') : t('common.saveChanges')}
          </button>

          <BotNav />
        </>
      )}
    </BotGate>
  );
}
